"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Calendar,
  CalendarCheck,
  Clock,
  CreditCard,
  Heart,
  LogOut,
  MapPin,
  Sparkles,
  Video,
} from "lucide-react";
import { useAuth } from "./context/AuthContext";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { Badge } from "./ui/badge";
import api from "../lib/api";
import EnhancedBookingModal from "./EnhancedBookingModal";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Avatar, AvatarFallback } from "./ui/avatar";
import PaymentMethodCollection from "./PaymentMethodCollection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "./ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { cn } from "../lib/utils";
import { NavbarDemo } from "./NavBar";
import ScheduleView from "./ScheduleView";
import { API } from "@/config/api.config";
import ReassignmentApprovalModal from "./ReassignmentApprovalModal";

const currencyFormatter = new Intl.NumberFormat("en-CA", {
  style: "currency",
  currency: "CAD",
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};

const roundCurrency = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 0;
  }
  return Math.round(numeric * 100) / 100;
};

const formatInvoiceDateTime = (value) => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? value : "N/A";
  }
  return date.toLocaleString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const sanitizeFilePart = (value) =>
  value
    ?.toString()
    ?.replace(/[^\w\s-]/g, "")
    ?.trim()
    ?.replace(/\s+/g, "_") || "client";

const formatDateLabel = (isoString) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(new Date(isoString));
  } catch (error) {
    return isoString;
  }
};

const formatTimeLabel = (isoString) => {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(isoString));
  } catch (error) {
    return "";
  }
};

const getInitials = (name) =>
  name
    .split(/\s+/)
    .map((part) => part?.[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("") || "YOU";

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  console.log({ user });

  const fullName = user?.fullName?.trim?.() || "Explorer";
  const accountHolderName = fullName;
  const accountEmail = user?.email || "you@bookatherapy.app";

  // State for appointments and favorites
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [historyAppointments, setHistoryAppointments] = useState([]);
  const [upcomingAppointmentsRaw, setUpcomingAppointmentsRaw] = useState([]);
  const [historyAppointmentsRaw, setHistoryAppointmentsRaw] = useState([]);
  const [favoriteTherapists, setFavoriteTherapists] = useState([]);
  const [isLoadingAppointments, setIsLoadingAppointments] = useState(true);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const [appointmentsError, setAppointmentsError] = useState(null);

  const [favoritesError, setFavoritesError] = useState(null);

  // Cancel/Reschedule dialogs
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    appointmentId: null,
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const [rescheduleDialog, setRescheduleDialog] = useState({
    open: false,
    appointment: null,
  });
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({});
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [scheduleStartDate, setScheduleStartDate] = useState(new Date());
  const [scheduleData, setScheduleData] = useState({});
  const [newAppointmentDateTime, setNewAppointmentDateTime] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceBookingId, setInvoiceBookingId] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const invoiceRef = useRef(null);
  const [savedCard, setSavedCard] = useState(null);
  const [isLoadingCard, setIsLoadingCard] = useState(true);
  const [updateCardDialog, setUpdateCardDialog] = useState({ open: false });
  const [operatingHours, setOperatingHours] = useState(null);
  const [therapistAvailability, setTherapistAvailability] = useState(null);
  const [addonMap, setAddonMap] = useState(new Map());

  // Booking modal state
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedTherapistForBooking, setSelectedTherapistForBooking] =
    useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [bookingDate, setBookingDate] = useState(null);
  const [bookingTime, setBookingTime] = useState(null);
  const [bookingSlots, setBookingSlots] = useState([]);
  const [isLoadingBookingData, setIsLoadingBookingData] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [paymentOption, setPaymentOption] = useState("payAtClinic");
  const [useFreeMassage, setUseFreeMassage] = useState(false);
  const [freeMassagesAvailable, setFreeMassagesAvailable] = useState(0);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [giftCardCode, setGiftCardCode] = useState("");
  const [giftCardValidating, setGiftCardValidating] = useState(false);
  const [giftCardValid, setGiftCardValid] = useState(null);
  const [giftCardError, setGiftCardError] = useState("");
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [giftCardApplied, setGiftCardApplied] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [bookingError, setBookingError] = useState("");

  // Reassignment state
  const [pendingReassignments, setPendingReassignments] = useState([]);
  const [reassignmentModalOpen, setReassignmentModalOpen] = useState(false);
  const [selectedReassignment, setSelectedReassignment] = useState(null);
  const [isLoadingReassignments, setIsLoadingReassignments] = useState(false);

  const upcomingCount = upcomingAppointments.length;
  const favoritesCount = favoriteTherapists.length;

  const fetchAndSetAppointments = useCallback(
    async (showSpinner = true) => {
      if (!user) {
        setIsLoadingAppointments(false);
        return;
      }

      try {
        if (showSpinner) setIsLoadingAppointments(true);
        setAppointmentsError(null);

        const data = await api.appointments.getMyAppointments();

        const parseMs = (iso) => {
          try {
            const t = Date.parse(iso);
            return Number.isNaN(t) ? null : t; // UTC-safe comparison
          } catch {
            return null;
          }
        };

        const upcomingRaw = data?.upcoming || [];
        const historyRaw = data?.history || [];

        setUpcomingAppointmentsRaw(upcomingRaw);
        setHistoryAppointmentsRaw(historyRaw);

        const upcoming = upcomingRaw
          .map((apt) => {
            const therapistName =
              `${apt?.therapist?.fullName}`.trim() || "Therapist";
            const serviceName =
              apt?.therapist?.bioOneLiner ||
              apt?.therapist?.bio?.substring(0, 100) ||
              "no bio available";
            const startsAtISO = apt?.appointmentDate;
            const startsAtMs = startsAtISO ? parseMs(startsAtISO) : null;
            const rawStatus = (apt?.status || "").toLowerCase();
            let displayStatus = "Upcoming";
            if (rawStatus === "confirmed") displayStatus = "confirmed";
            else if (rawStatus === "pending") displayStatus = "pending";
            return {
              _id: apt?._id || `${startsAtISO || "unknown"}-${therapistName}`,
              therapistName,
              serviceName,
              startsAtISO,
              startsAtMs,
              status: displayStatus,
              durationMins: apt?.duration || 60,
              clinicName: apt?.clinic?.clinicName || null,
              notes: apt?.notes || "",
              rating: apt?.therapist?.rating || 0,
            };
          })
          .sort(
            (a, b) => (a.startsAtMs ?? Infinity) - (b.startsAtMs ?? Infinity),
          );

        const history = historyRaw
          .map((apt) => {
            const therapistName =
              `${apt?.therapist?.fullName}`.trim() || "Therapist";
            const serviceName =
              apt?.therapist?.bioOneLiner ||
              apt?.therapist?.bio?.substring(0, 100) ||
              "no bio available";
            const startsAtISO = apt?.appointmentDate;
            const startsAtMs = startsAtISO ? parseMs(startsAtISO) : null;
            const rawStatus = (apt?.status || "").toLowerCase();
            let displayStatus = "Completed";
            if (rawStatus === "cancelled") displayStatus = "Cancelled";
            else if (rawStatus === "completed") displayStatus = "Completed";
            return {
              _id: apt?._id || `${startsAtISO || "unknown"}-${therapistName}`,
              therapistName,
              serviceName,
              startsAtISO,
              startsAtMs,
              status: displayStatus,
              durationMins: apt?.duration || 60,
              clinicName: apt?.clinic?.name || null,
              notes: apt?.notes || "",
              rating: apt?.therapist?.rating || 0,
            };
          })
          .sort(
            (a, b) => (b.startsAtMs ?? -Infinity) - (a.startsAtMs ?? -Infinity),
          );

        const transformedUpcoming = upcoming.map((n, index) => {
          const rawApt = upcomingRaw[index];
          return {
            id: n._id,
            therapist: n.therapistName,
            therapistId: rawApt?.therapist?._id || null,
            therapistTagline: n.serviceName,
            focus: rawApt?.therapist?.massageTypes || [],
            status: n.status,
            location: n.clinicName || "Virtual",
            startsAt: n.startsAtISO,
            duration: `${n.durationMins} minutes`,
            notes: n.notes,
          };
        });

        const transformedHistory = history.map((n) => ({
          id: n._id,
          therapist: n.therapistName,
          completedAt: n.startsAtISO,
          focus: n.serviceName ? n.serviceName : "Therapy session",
          moodShift: "",
          rating: n.rating || 0,
          status: n.status,
        }));

        setUpcomingAppointments(transformedUpcoming);
        setHistoryAppointments(transformedHistory);
      } catch (error) {
        console.error("Failed to fetch appointments:", error);
        setAppointmentsError(error.message || "Failed to load appointments");
        setUpcomingAppointments([]);
        setHistoryAppointments([]);
      } finally {
        if (showSpinner) setIsLoadingAppointments(false);
      }
    },
    [user],
  );

  // Fetch appointments on component mount
  useEffect(() => {
    fetchAndSetAppointments(true);
  }, [fetchAndSetAppointments]);

  // Fetch pending reassignments and handle deep links from email
  useEffect(() => {
    const fetchPendingReassignments = async () => {
      if (!user?.email) {
        setIsLoadingReassignments(false);
        return;
      }

      try {
        setIsLoadingReassignments(true);
        const data = await api.appointments.getPendingReassignments();
        setPendingReassignments(data.pendingReassignments || []);

        // Check URL params for deep link from email
        const urlParams = new URLSearchParams(window.location.search);
        const reassignmentBookingId = urlParams.get("reassignment");
        const action = urlParams.get("action"); // "accept" or "reject"

        if (reassignmentBookingId) {
          const reassignment = data.pendingReassignments?.find(
            (r) => r.bookingId === reassignmentBookingId,
          );

          if (reassignment) {
            setSelectedReassignment(reassignment);
            setReassignmentModalOpen(true);

            // Handle direct action from email link
            if (action === "accept") {
              handleAcceptReassignment(reassignment.bookingId);
            } else if (action === "reject") {
              handleRejectReassignment(reassignment.bookingId);
            }

            // Clean up URL params
            window.history.replaceState({}, "", window.location.pathname);
          }
        }
      } catch (error) {
        console.error("Failed to fetch pending reassignments:", error);
        setPendingReassignments([]);
      } finally {
        setIsLoadingReassignments(false);
      }
    };

    fetchPendingReassignments();
  }, [user]);

  // Handler for accepting reassignment
  const handleAcceptReassignment = async (bookingId) => {
    try {
      await api.appointments.acceptReassignment(bookingId);

      // Refresh appointments and reassignments
      await fetchAndSetAppointments(false);

      // Remove from pending reassignments
      setPendingReassignments((prev) =>
        prev.filter((r) => r.bookingId !== bookingId),
      );

      toast({
        title: "Reassignment accepted",
        description:
          "Your appointment has been confirmed with the new therapist.",
      });

      setReassignmentModalOpen(false);
      setSelectedReassignment(null);
    } catch (error) {
      console.error("Failed to accept reassignment:", error);
      throw error;
    }
  };

  // Handler for rejecting reassignment
  const handleRejectReassignment = async (bookingId) => {
    try {
      await api.appointments.rejectReassignment(bookingId);

      // Refresh appointments and reassignments
      await fetchAndSetAppointments(false);

      // Remove from pending reassignments
      setPendingReassignments((prev) =>
        prev.filter((r) => r.bookingId !== bookingId),
      );

      toast({
        title: "Refund requested",
        description:
          "Your appointment has been cancelled and a full refund will be processed.",
      });

      setReassignmentModalOpen(false);
      setSelectedReassignment(null);
    } catch (error) {
      console.error("Failed to reject reassignment:", error);
      throw error;
    }
  };

  // Fetch favorite therapists on component mount
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user) {
        setIsLoadingFavorites(false);
        return;
      }

      try {
        setIsLoadingFavorites(true);
        setFavoritesError(null);

        const data = await api.user.getFavoriteTherapists();
        console.log("📥 Fetched favorite therapists:", data.favorites);

        // Fetch additional data for each favorite therapist (reviews, last session, etc.)
        const enrichedFavorites = await Promise.all(
          (data.favorites || []).map(async (therapist) => {
            console.log(
              `🔄 Enriching data for ${therapist.fullName} (${therapist._id})`,
            );
            try {
              // Fetch therapist reviews to get actual rating
              const reviewsData = await api.therapist.getTherapistReviews(
                therapist._id,
              );
              const reviews = reviewsData.reviews || [];
              const actualRating =
                reviews.length > 0
                  ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) /
                    reviews.length
                  : therapist.rating || 0;

              console.log(
                `⭐ ${therapist.fullName} rating: ${actualRating.toFixed(1)} (${
                  reviews.length
                } reviews)`,
              );

              // Fetch last session from appointments history
              let lastSession = "";
              try {
                const appointmentsData = await api.booking.getAppointments();
                const allAppointments = [
                  ...(appointmentsData.upcomingAppointments || []),
                  ...(appointmentsData.pastAppointments || []),
                ];

                console.log(`📅 Total appointments: ${allAppointments.length}`);

                // Find last completed session with this therapist
                const lastAppointment = allAppointments
                  .filter(
                    (apt) =>
                      apt.therapistId === therapist._id &&
                      apt.status === "completed" &&
                      new Date(apt.startsAt) < new Date(),
                  )
                  .sort(
                    (a, b) => new Date(b.startsAt) - new Date(a.startsAt),
                  )[0];

                if (lastAppointment) {
                  const date = new Date(lastAppointment.startsAt);
                  lastSession = date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  });
                  console.log(
                    `📆 ${therapist.fullName} last session: ${lastSession}`,
                  );
                } else {
                  console.log(
                    `📆 ${therapist.fullName}: No previous sessions found`,
                  );
                }
              } catch (error) {
                console.error(
                  `❌ Error fetching last session for ${therapist._id}:`,
                  error,
                );
              }

              // Fetch next availability
              let nextAvailable = "";
              try {
                const today = new Date();
                const dateISO = today.toISOString().split("T")[0];
                console.log(
                  `🔍 Checking availability for ${therapist.fullName} on ${dateISO}`,
                );

                const availResponse = await fetch(
                  `${API}/availability/slots?setting=Clinic&date=${dateISO}&therapistId=${therapist._id}`,
                );
                const availData = await availResponse.json();

                console.log(
                  `📊 Availability data for ${therapist.fullName}:`,
                  availData,
                );

                if (availData.slots && availData.slots.length > 0) {
                  // Get the first available slot
                  nextAvailable = `Today at ${availData.slots[0]}`;
                  console.log(
                    `✅ ${therapist.fullName} next available: ${nextAvailable}`,
                  );
                } else {
                  // Check tomorrow
                  const tomorrow = new Date(today);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const tomorrowISO = tomorrow.toISOString().split("T")[0];
                  console.log(
                    `🔍 No slots today, checking tomorrow ${tomorrowISO}`,
                  );

                  const tomorrowResponse = await fetch(
                    `${API}/availability/slots?setting=Clinic&date=${tomorrowISO}&therapistId=${therapist._id}`,
                  );
                  const tomorrowData = await tomorrowResponse.json();

                  if (tomorrowData.slots && tomorrowData.slots.length > 0) {
                    nextAvailable = `Tomorrow at ${tomorrowData.slots[0]}`;
                    console.log(
                      `✅ ${therapist.fullName} next available: ${nextAvailable}`,
                    );
                  } else {
                    console.log(
                      `⚠️ ${therapist.fullName}: No availability for next 2 days`,
                    );
                  }
                }
              } catch (error) {
                console.error(
                  `❌ Error fetching availability for ${therapist._id}:`,
                  error,
                );
              }

              return {
                id: therapist._id,
                name: therapist.fullName?.trim() || "Therapist",
                title: therapist.bioOneLiner || "no bio available",
                rating: actualRating,
                reviewCount: reviews.length,
                matchScore: 95, // Default match score
                lastSession,
                nextAvailable,
                avatarClass: "bg-palette-navy/10 text-palette-navy",
                profilePhotoUrl: therapist.profilePhotoUrl,
                massageTypes: therapist.massageTypes || [],
                settings: therapist.settings || [],
                availableSettings: therapist.settings || [],
              };
            } catch (error) {
              console.error(
                `Error enriching therapist ${therapist._id}:`,
                error,
              );
              return {
                id: therapist._id,
                name: therapist.fullName?.trim() || "Therapist",
                title: therapist.bioOneLiner || "no bio available",
                rating: therapist.rating || 0,
                reviewCount: 0,
                matchScore: 95,
                lastSession: "",
                nextAvailable: "",
                avatarClass: "bg-palette-navy/10 text-palette-navy",
                profilePhotoUrl: therapist.profilePhotoUrl,
                massageTypes: therapist.massageTypes || [],
                settings: therapist.settings || [],
                availableSettings: therapist.settings || [],
              };
            }
          }),
        );

        console.log("✅ Enriched favorites data:", enrichedFavorites);
        setFavoriteTherapists(enrichedFavorites);
      } catch (error) {
        console.error("❌ Failed to fetch favorite therapists:", error);
        setFavoritesError(error.message || "Failed to load favorites");
        setFavoriteTherapists([]);
      } finally {
        setIsLoadingFavorites(false);
      }
    };

    fetchFavorites();
  }, [user]);

  // Fetch user's rewards to check for free massages
  useEffect(() => {
    const fetchRewards = async () => {
      if (!user?.email) {
        setLoadingRewards(false);
        return;
      }
      try {
        setLoadingRewards(true);
        const rewards = await api.rewards.getRewards();
        if (rewards?.success && rewards?.rewards) {
          setFreeMassagesAvailable(rewards.rewards.freeMassagesAvailable || 0);
        }
      } catch (err) {
        console.error("Failed to fetch rewards:", err);
        setFreeMassagesAvailable(0);
      } finally {
        setLoadingRewards(false);
      }
    };

    fetchRewards();
  }, [user]);

  // Fetch saved payment method
  useEffect(() => {
    const fetchSavedCard = async () => {
      if (!user || !accountEmail) {
        setIsLoadingCard(false);
        return;
      }

      try {
        setIsLoadingCard(true);
        const data = await api.payment.getSavedPaymentMethod(accountEmail);
        if (data?.paymentMethod) {
          const pm = data.paymentMethod;
          setSavedCard({
            brand: pm.card?.brand || "Card",
            last4: pm.card?.last4 || "",
            expMonth: pm.card?.exp_month || null,
            expYear: pm.card?.exp_year || null,
            isDefault: data.isDefault || false,
          });
        } else {
          setSavedCard(null);
        }
      } catch (error) {
        console.error("Failed to fetch saved card:", error);
        // Card might not exist yet, that's okay
        setSavedCard(null);
      } finally {
        setIsLoadingCard(false);
      }
    };

    fetchSavedCard();
  }, [user, accountEmail]);

  // Fetch services and set default setting when booking modal opens
  useEffect(() => {
    const fetchServices = async () => {
      if (!bookingModalOpen || !selectedTherapistForBooking) return;

      try {
        setIsLoadingBookingData(true);

        // Set default setting if therapist has settings
        const therapistSettings =
          selectedTherapistForBooking.settings ||
          selectedTherapistForBooking.availableSettings ||
          [];
        if (therapistSettings.length > 0 && !selectedSetting) {
          setSelectedSetting(therapistSettings[0]);
        }

        console.log(
          "🔍 Fetching services for therapist:",
          selectedTherapistForBooking,
        );

        // Fetch all available services using the api
        const data = await api.pricing.getServices();
        console.log("📦 All services received:", data);

        // Get therapist's massage types (service names, not IDs)
        const therapistServiceNames =
          selectedTherapistForBooking.massageTypes || [];
        console.log("🎯 Therapist service names:", therapistServiceNames);

        // Filter services to only show what this therapist offers
        let services = data.services || data || [];

        // Match by service name since massageTypes contains names
        if (therapistServiceNames.length > 0) {
          services = services.filter((service) => {
            // Match against service name or title
            const serviceName =
              service.name || service.title || service.serviceName || "";
            const match = therapistServiceNames.includes(serviceName);
            console.log(
              `  Checking service "${serviceName}": ${match ? "✅" : "❌"}`,
            );
            return match;
          });
        }

        console.log("✅ Filtered services for therapist:", services);
        setAvailableServices(services);

        if (services.length === 0) {
          toast({
            title: "No services available",
            description: "This therapist hasn't configured their services yet.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch services:", error);
        toast({
          title: "Error",
          description: `Failed to load services: ${error.message}`,
          variant: "destructive",
        });
        setAvailableServices([]);
      } finally {
        setIsLoadingBookingData(false);
      }
    };

    fetchServices();
  }, [bookingModalOpen, selectedTherapistForBooking, toast]);

  // Fetch availability when service and date are selected
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedService || !bookingDate || !selectedTherapistForBooking) {
        setBookingSlots([]);
        return;
      }

      try {
        setIsLoadingBookingData(true);
        const dateISO = bookingDate.toISOString().split("T")[0];

        // Fetch availability slots for the therapist
        const response = await fetch(
          `${API}/availability/slots?` +
            `setting=Clinic&` +
            `date=${dateISO}&` +
            `therapistId=${selectedTherapistForBooking.id}`,
        );
        const data = await response.json();

        // Extract time slots
        const slots = data.slots || [];
        setBookingSlots(slots);
      } catch (error) {
        console.error("Failed to fetch availability:", error);
        toast({
          title: "Error",
          description: "Failed to load available times. Please try again.",
          variant: "destructive",
        });
        setBookingSlots([]);
      } finally {
        setIsLoadingBookingData(false);
      }
    };

    fetchAvailability();
  }, [selectedService, bookingDate, selectedTherapistForBooking, toast]);

  const handleLogout = () => {
    logout();
    toast({
      title: "Signed out",
      description: "You have been securely logged out.",
      duration: 3000,
    });
    navigate("/");
  };

  // Payment success handler
  const handlePaymentSuccess = (info) => {
    console.log("💳 Payment info received:", info);
    setPaymentInfo(info);
    setBookingError("");
  };

  // Gift card validation
  const handleValidateGiftCard = async () => {
    if (!giftCardCode.trim()) {
      setGiftCardError("Please enter a gift card code");
      return;
    }

    try {
      setGiftCardValidating(true);
      setGiftCardError("");

      const response = await api.giftCards.validate(
        giftCardCode.trim().toUpperCase(),
      );

      if (response.valid && response.balance > 0) {
        setGiftCardValid(true);
        setGiftCardAmount(response.balance);
        setGiftCardApplied(true);
        setGiftCardError("");
        toast({
          title: "Gift card applied",
          description: `$${response.balance.toFixed(2)} credit available`,
        });
      } else {
        setGiftCardValid(false);
        setGiftCardError("Invalid or expired gift card code");
        setGiftCardAmount(0);
        setGiftCardApplied(false);
      }
    } catch (error) {
      console.error("Gift card validation error:", error);
      setGiftCardValid(false);
      setGiftCardError(error.message || "Failed to validate gift card");
      setGiftCardAmount(0);
      setGiftCardApplied(false);
    } finally {
      setGiftCardValidating(false);
    }
  };

  // Handle booking confirmation
  const handleBookingConfirm = async () => {
    try {
      setIsLoadingBookingData(true);
      setBookingError("");

      const dateISO = bookingDate.toISOString().split("T")[0];
      const setting =
        selectedSetting ||
        selectedTherapistForBooking.settings?.[0] ||
        selectedTherapistForBooking.availableSettings?.[0] ||
        "Clinic";

      // Calculate amounts first to check if fully covered
      const servicePrice = selectedDuration.price;
      const giftCardDiscount = giftCardApplied
        ? Math.min(giftCardAmount, servicePrice)
        : 0;
      const subtotal = servicePrice - giftCardDiscount;
      const isFullyCovered =
        useFreeMassage || (giftCardApplied && subtotal <= 0);

      // Check if payment is needed but not provided
      if (!useFreeMassage && !isFullyCovered && !paymentInfo && !savedCard) {
        setBookingError("Please complete payment before confirming booking");
        setIsLoadingBookingData(false);
        return;
      }

      console.log("🔄 Creating booking hold...", {
        therapist: selectedTherapistForBooking.name,
        service: selectedService.name,
        date: dateISO,
        time: bookingTime,
        setting,
        useFreeMassage,
        giftCardApplied,
      });

      // Step 1: Create a hold
      const holdPayload = {
        therapistId: selectedTherapistForBooking.id,
        setting,
        dateISO,
        time: bookingTime,
        clientEmail: accountEmail,
        clientName: user?.fullName || accountEmail,
        serviceId: selectedService._id || selectedService.id,
        serviceName: selectedService.name,
        durationMinutes: selectedDuration.duration,
      };

      const holdResponse = await api.hold(holdPayload);
      console.log("✅ Hold created:", holdResponse);

      // Calculate GST and total
      const gstAmount = subtotal * 0.05;
      const totalAmount = subtotal + gstAmount;

      // Step 2: Confirm the hold
      let paymentInfoToSend = null;
      if (!useFreeMassage && !isFullyCovered) {
        if (savedCard) {
          // Using saved card
          paymentInfoToSend = {
            email: accountEmail,
            useDefaultPaymentMethod: true,
            paymentOption,
          };
        } else if (paymentInfo) {
          // Using new payment method
          paymentInfoToSend = {
            ...paymentInfo,
            email: accountEmail,
            paymentOption,
          };
        }
      }

      const confirmPayload = {
        holdId: holdResponse.holdId,
        paymentInfo: paymentInfoToSend,
        useFreeMassage: useFreeMassage || undefined,
        giftCardCode: giftCardApplied
          ? giftCardCode.trim().toUpperCase()
          : undefined,
        giftCardDiscount: giftCardApplied ? giftCardDiscount : undefined,
        totalAmount,
        serviceDetails: {
          serviceId: selectedService._id || selectedService.id,
          serviceName: selectedService.name,
          servicePrice: selectedDuration.price,
          durationMinutes: selectedDuration.duration,
          gstAmount,
          gstRate: 0.05,
        },
      };

      console.log("💳 Confirming booking...", confirmPayload);
      const confirmResponse = await api.confirm(confirmPayload);
      console.log("✅ Booking confirmed:", confirmResponse);

      // If using free massage, redeem it
      if (useFreeMassage) {
        try {
          await api.rewards.redeemFreeMassage();
          setFreeMassagesAvailable((prev) => Math.max(0, prev - 1));
        } catch (error) {
          console.error("Failed to redeem free massage:", error);
        }
      }

      toast({
        title: "Booking confirmed!",
        description: `Your appointment with ${selectedTherapistForBooking?.name} on ${dateISO} at ${bookingTime} has been booked.`,
      });

      // Refresh appointments
      await fetchAndSetAppointments(false);

      // Close modal and reset
      setBookingModalOpen(false);
      setSelectedService(null);
      setSelectedDuration(null);
      setBookingDate(null);
      setBookingTime(null);
      setSelectedSetting(null);
      setUseFreeMassage(false);
      setGiftCardCode("");
      setGiftCardValid(null);
      setGiftCardApplied(false);
      setGiftCardAmount(0);
      setPaymentOption("payAtClinic");
      setPaymentInfo(null);
      setBookingError("");
    } catch (error) {
      console.error("❌ Failed to create booking:", error);
      toast({
        title: "Booking failed",
        description:
          error.message || "Failed to create booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBookingData(false);
    }
  };

  const handleToggleFavorite = async (therapistId, isFavorite) => {
    try {
      if (isFavorite) {
        // Remove from favorites
        await api.user.removeFavoriteTherapist(therapistId);
        setFavoriteTherapists((prev) =>
          prev.filter((t) => t.id !== therapistId),
        );
        toast({
          title: "Removed from favorites",
          description: "Therapist removed from your favorites list.",
        });
      } else {
        // Add to favorites
        await api.user.addFavoriteTherapist(therapistId);
        toast({
          title: "Added to favorites",
          description: "Therapist added to your favorites list.",
        });
        // Refresh favorites list
        const data = await api.user.getFavoriteTherapists();
        const transformed = (data.favorites || []).map((therapist) => ({
          id: therapist._id,
          name: therapist.fullName?.trim() || "Therapist",
          title: therapist.bioOneLiner || "no bio available",
          focus: therapist.massageTypes ? [therapist.massageTypes] : [],
          rating: therapist.rating || 0,
          matchScore: 95,
          lastSession: "",
          nextAvailable: "",
          avatarClass: "bg-palette-navy/10 text-palette-navy",
          profilePhotoUrl: therapist.profilePhotoUrl,
        }));
        setFavoriteTherapists(transformed);
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update favorites",
        variant: "destructive",
      });
    }
  };

  const nextAppointment = upcomingAppointments[0];

  const canModifyAppointment = (isoString) => {
    if (!isoString) return false;
    const startsMs = Date.parse(isoString);
    if (Number.isNaN(startsMs)) return false;
    const diffMs = startsMs - Date.now();
    return diffMs >= 2 * 60 * 60 * 1000; // 2 hours
  };

  const openCancelFor = (appointmentId) =>
    setCancelDialog({ open: true, appointmentId });
  const closeCancelDialog = () => {
    setCancelDialog({ open: false, appointmentId: null });
    setIsCancelling(false);
  };

  const handleConfirmCancel = async () => {
    const id = cancelDialog.appointmentId;
    if (!id) return closeCancelDialog();

    setIsCancelling(true);

    // Snapshot for rollback
    const prevUpcoming = [...upcomingAppointments];
    const prevHistory = [...historyAppointments];

    // Optimistic UI update - do this immediately
    const cancelled = prevUpcoming.find((a) => a.id === id) || null;
    setUpcomingAppointments((prev) => prev.filter((a) => a.id !== id));
    if (cancelled) {
      setHistoryAppointments((prev) => [
        {
          id,
          therapist: cancelled.therapist,
          completedAt: cancelled.startsAt,
          focus: cancelled.therapistTagline || "Therapy session",
          moodShift: "",
          rating: 0,
          status: "Cancelled",
        },
        ...prev,
      ]);
    }

    // Perform async operations
    try {
      await api.appointments.cancelAppointment(id);

      toast({
        title: "Appointment cancelled",
        description: "We have processed your cancellation.",
      });

      // Re-fetch from backend to reflect real-time server state
      await fetchAndSetAppointments(false);
    } catch (error) {
      console.error("Cancel failed", error);
      // Rollback optimistic updates
      setUpcomingAppointments(prevUpcoming);
      setHistoryAppointments(prevHistory);
      toast({
        title: "Error",
        description: error.message || "Failed to cancel appointment",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
      closeCancelDialog();
    }
  };

  const openRescheduleFor = (appointment) => {
    // Navigate to reschedule page instead of opening modal
    navigate("/reschedule", {
      state: {
        appointment: {
          id: appointment.id,
          therapistId: appointment.therapistId,
          therapist: appointment.therapist,
          startsAt: appointment.startsAt,
          duration: appointment.duration
            ? parseInt(appointment.duration.replace(/\D/g, "")) || 60
            : 60,
          notes: appointment.notes || "",
          status: appointment.status || "pending",
        },
      },
    });
  };

  const closeRescheduleDialog = () => {
    setRescheduleDialog({ open: false, appointment: null });
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots({});
    setScheduleData({});
    setNewAppointmentDateTime(null);
    setIsRescheduling(false);
  };

  // Fetch therapist schedule data including availability and breaks
  const fetchTherapistScheduleData = async (appointment) => {
    if (!appointment?.therapistId) {
      toast({
        title: "Error",
        description: "Therapist information not available",
        variant: "destructive",
      });
      return;
    }

    setIsLoadingSlots(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Calculate date range: from 14 days before startDate (but not before today) to 21 days after startDate
      const displayStartDate = new Date(scheduleStartDate);
      displayStartDate.setDate(displayStartDate.getDate() - 14);

      // Don't fetch dates before today
      const fetchStartDate =
        displayStartDate < today ? today : displayStartDate;

      const fetchEndDate = new Date(scheduleStartDate);
      fetchEndDate.setDate(fetchEndDate.getDate() + 21); // 21 days into the future

      // Fetch clinic operating hours (needed for time slot generation)
      try {
        // Try to get clinic ID from appointment or localStorage
        const clinicId =
          appointment?.clinicId ||
          localStorage.getItem("clinic_id") ||
          (typeof process !== "undefined" &&
            (process.env?.NEXT_PUBLIC_DEFAULT_CLINIC_ID ||
              process.env?.REACT_APP_DEFAULT_CLINIC_ID)) ||
          "";

        if (clinicId) {
          const token = localStorage.getItem("bookatherapy_token");
          const res = await fetch(`${API}/clinic/${clinicId}`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
          });
          if (res.ok) {
            const clinicData = await res.json();
            if (clinicData?.operatingHours) {
              setOperatingHours(clinicData.operatingHours);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to fetch clinic operating hours:", error);
        // Continue without operating hours - will use defaults
      }

      // Fetch therapist availability schedule (needed for availability validation)
      try {
        const token = localStorage.getItem("bookatherapy_token");
        const res = await fetch(
          `${API}/availability/${appointment.therapistId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: "include",
          },
        );
        if (res.ok) {
          const availabilityData = await res.json();
          if (availabilityData?.availability) {
            setTherapistAvailability(availabilityData.availability);
          }
        }
      } catch (error) {
        console.warn("Failed to fetch therapist availability:", error);
        // Continue without availability - will prevent all drops
      }

      // Fetch availability for the extended period
      const availability = await api.appointments.fetchTherapistAvailability(
        appointment.therapistId,
        fetchStartDate.toISOString(),
        fetchEndDate.toISOString(),
      );

      // Fetch away times (breaks/unavailability)
      const aways = await api.appointments.fetchTherapistAways(
        appointment.therapistId,
      );

      setAvailableSlots(availability);

      // Process schedule data - structure it with breaks/unavailability
      const processedSchedule = {};
      const dates = [];
      const current = new Date(fetchStartDate);
      const end = new Date(fetchEndDate);

      while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }

      // Helper to check if an away applies to a specific date
      const doesAwayApplyToDate = (away, dateStr, date) => {
        if (!away.from || !away.to) return false;

        const awayFromDate = new Date(away.from.split("T")[0]);
        const awayToDate = new Date(away.to.split("T")[0]);
        const currentDate = new Date(dateStr);

        if (currentDate < awayFromDate || currentDate > awayToDate) {
          return false;
        }

        // Check repeat pattern
        if (away.repeat === "no-repeat") {
          return dateStr === away.from.split("T")[0];
        } else if (away.repeat === "repeat-daily") {
          return true; // Applies every day in range
        } else if (away.repeat === "repeat-weekly") {
          const awayDayOfWeek = awayFromDate.getDay();
          const currentDayOfWeek = currentDate.getDay();
          return awayDayOfWeek === currentDayOfWeek;
        }

        return false;
      };

      // Process each date's data
      dates.forEach((date) => {
        const dateStr = date.toISOString().split("T")[0];
        const slots = availability[dateStr] || [];

        // Filter aways that apply to this date
        const dateAways = aways.filter((away) =>
          doesAwayApplyToDate(away, dateStr, date),
        );

        // Map aways to break/unavailability format
        const breaks = dateAways
          .filter((away) => away.type === "break")
          .map((away) => ({
            startTime: away.startTimeSlot,
            endTime:
              away.endTimeSlot || addMinutes(away.startTimeSlot, away.duration),
            duration: away.duration,
            note: away.note || "",
            type: away.type,
          }));

        const unavailability = dateAways
          .filter((away) => away.type !== "break")
          .map((away) => ({
            startTime: away.startTimeSlot,
            endTime:
              away.endTimeSlot || addMinutes(away.startTimeSlot, away.duration),
            duration: away.duration,
            note: away.note || "",
            type: away.type,
          }));

        processedSchedule[dateStr] = {
          slots,
          breaks,
          unavailability,
        };
      });

      setScheduleData(processedSchedule);
    } catch (error) {
      console.error("Failed to fetch therapist schedule:", error);
      toast({
        title: "Error",
        description: "Failed to load schedule data",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Helper function to add minutes to time
  const addMinutes = (hhmm, mins) => {
    const [h, m] = hhmm.split(":").map(Number);
    const total = h * 60 + m + mins;
    const H = Math.floor(total / 60);
    const M = total % 60;
    return `${String(H).padStart(2, "0")}:${String(M).padStart(2, "0")}`;
  };

  // Check if a date has available slots
  const dateHasAvailableSlots = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    const slots = availableSlots[dateStr] || [];
    return slots.length > 0;
  };

  // Check if date is in the past
  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Format date for display
  const formatDateDisplay = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    if (!time) return "";
    // Handle both ISO string and time string formats
    if (time.includes("T")) {
      return new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(time));
    }
    // Handle HH:MM format
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  // Handle appointment move from ScheduleView (using PUT API like page.tsx)
  const handleAppointmentMove = async (newDateTime) => {
    if (!rescheduleDialog.appointment || !newDateTime) return;

    setIsRescheduling(true);
    try {
      const appointment = rescheduleDialog.appointment;
      const durationMins = appointment.duration
        ? parseInt(appointment.duration.replace(/\D/g, "")) || 60
        : 60;

      // Use PUT API like page.tsx does for updating appointments
      // Get client name from user context (since this is client-side)
      const clientName = accountHolderName || user?.email || "Client";

      const payload = {
        clientName: clientName,
        therapistId: appointment.therapistId,
        appointmentDate: newDateTime.toISOString(),
        duration: durationMins,
        notes: appointment.notes || "",
        status: appointment.status || "pending",
      };

      // Use the update endpoint with PUT method (matching page.tsx)
      const response = await api.appointments.updateAppointment(
        appointment.id,
        payload,
      );

      toast({
        title: "Appointment rescheduled",
        description: `Successfully rescheduled to ${formatDateDisplay(
          newDateTime,
        )} at ${formatTimeDisplay(newDateTime.toISOString())}`,
      });

      // Refresh appointments to reflect changes
      await fetchAndSetAppointments(false);
      closeRescheduleDialog();
    } catch (error) {
      console.error("Reschedule failed:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to reschedule appointment",
        variant: "destructive",
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  // Handle reschedule confirmation (legacy - may still be used)
  const handleRescheduleConfirm = async () => {
    if (!selectedDate || !selectedTime || !rescheduleDialog.appointment) return;

    setIsRescheduling(true);
    try {
      const appointment = rescheduleDialog.appointment;
      const newDateTime = new Date(selectedDate);

      // Parse time - handle both ISO string and HH:MM format
      let hours, minutes;
      if (selectedTime.includes("T")) {
        const timeDate = new Date(selectedTime);
        hours = timeDate.getHours();
        minutes = timeDate.getMinutes();
      } else {
        [hours, minutes] = selectedTime.split(":").map(Number);
      }

      newDateTime.setHours(hours, minutes, 0, 0);

      await api.appointments.rescheduleAppointment(
        appointment.id,
        newDateTime.toISOString(),
      );

      toast({
        title: "Appointment rescheduled",
        description: `Successfully rescheduled to ${formatDateDisplay(
          selectedDate,
        )} at ${formatTimeDisplay(selectedTime)}`,
      });

      // Refresh appointments to reflect changes
      await fetchAndSetAppointments(false);
      closeRescheduleDialog();
    } catch (error) {
      console.error("Reschedule failed:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to reschedule appointment",
        variant: "destructive",
      });
    } finally {
      setIsRescheduling(false);
    }
  };

  /* ====== invoice helpers (align with clinic calendar) ====== */
  const mapRawAppointmentToEditing = useCallback((raw) => {
    if (!raw) return null;

    const id = raw?._id || raw?.id;
    const clientName =
      raw?.clientName ||
      raw?.client?.fullName ||
      `${raw?.client?.firstName || ""} ${raw?.client?.lastName || ""}`.trim() ||
      "Client";
    const clientEmail = raw?.clientEmail || raw?.client?.email || "";
    const clientPhone =
      raw?.clientPhone || raw?.client?.phone || raw?.client?.phoneNumber || "";
    const clientDob = raw?.clientDob || raw?.client?.dob || null;

    const appointmentDate = raw?.appointmentDate
      ? new Date(raw.appointmentDate)
      : null;

    const toHHMM = (date) => {
      if (!date) return "00:00";
      const local = new Date(date);
      const hours = local.getHours().toString().padStart(2, "0");
      const minutes = local.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    };

    const duration =
      typeof raw?.duration === "number"
        ? raw.duration
        : typeof raw?.durationMinutes === "number"
        ? raw.durationMinutes
        : raw?.service?.durationMinutes || 60;

    const startTime =
      raw?.startTime ||
      toHHMM(appointmentDate) ||
      (raw?.startsAt ? toHHMM(raw.startsAt) : "09:00");
    const endTime = raw?.endTime || addMinutes(startTime, duration) || "10:00";

    return {
      id,
      clientName,
      clientEmail,
      clientPhone,
      clientDob,
      service:
        raw?.service?.name ||
        raw?.serviceName ||
        raw?.therapistTagline ||
        raw?.focus ||
        "Session",
      duration,
      status: raw?.status || "completed",
      startTime,
      endTime,
      arrivedAt: raw?.arrivedAt,
      therapist:
        raw?.therapist?.fullName || raw?.therapistName || raw?.therapist || "",
      therapistId:
        raw?.therapist?._id || raw?.therapistId || raw?.therapist?._id || "",
      clinic: raw?.clinic || {},
      addOns: raw?.addOns || raw?.service?.addOns || [],
      paymentMethod: raw?.paymentMethod || raw?.payment?.paymentOption,
      payment: raw?.payment,
      insuranceDetails: raw?.insuranceDetails,
      appointmentDate: raw?.appointmentDate,
      notes: raw?.notes || "",
      invoice: raw?.invoice || null,
      focus: raw?.focus || [],
    };
  }, []);

  const findAppointmentById = useCallback(
    (bookingId) => {
      if (!bookingId) return null;
      const historyMatch = historyAppointmentsRaw.find(
        (apt) => apt?._id === bookingId || apt?.id === bookingId,
      );
      if (historyMatch) return mapRawAppointmentToEditing(historyMatch);
      const upcomingMatch = upcomingAppointmentsRaw.find(
        (apt) => apt?._id === bookingId || apt?.id === bookingId,
      );
      if (upcomingMatch) return mapRawAppointmentToEditing(upcomingMatch);
      return null;
    },
    [
      historyAppointmentsRaw,
      upcomingAppointmentsRaw,
      mapRawAppointmentToEditing,
    ],
  );

  const handleOpenInvoice = useCallback(
    async (targetAppointment) => {
      const appointment = targetAppointment || editingAppointment;
      if (!appointment?.id || invoiceLoading) {
        return;
      }

      setIsInvoiceOpen(true);
      setInvoiceError(null);

      if (invoiceBookingId === appointment.id && invoiceData) {
        return;
      }

      setInvoiceLoading(true);
      try {
        const res = await fetch(
          `${API}/bookings/clinic/${appointment.id}/invoice`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.message || "Failed to fetch invoice details");
        }

        const data = await res.json();
        setInvoiceData(data);
        setInvoiceBookingId(appointment.id);
      } catch (error) {
        console.error("Failed to fetch invoice:", error);
        setInvoiceError(error?.message || "Failed to load invoice data");
      } finally {
        setInvoiceLoading(false);
      }
    },
    [API, editingAppointment, invoiceBookingId, invoiceData, invoiceLoading],
  );

  const openInvoiceForBooking = useCallback(
    (bookingId) => {
      const appointment = findAppointmentById(bookingId);
      if (!appointment) {
        toast({
          title: "Invoice unavailable",
          description: "We couldn’t find that appointment.",
          variant: "destructive",
        });
        return;
      }
      setEditingAppointment(appointment);
      handleOpenInvoice(appointment);
    },
    [findAppointmentById, handleOpenInvoice, toast],
  );

  useEffect(() => {
    setInvoiceData(null);
    setInvoiceBookingId(null);
    setInvoiceError(null);
  }, [editingAppointment?.id]);

  useEffect(() => {
    let ignore = false;
    const loadAddons = async () => {
      try {
        const data = await api.pricing.getAddOns();
        const entries = Array.isArray(data)
          ? data
          : data?.addOns || data?.addons || [];
        if (!ignore) {
          const map = new Map();
          entries.forEach((addon) => {
            const id = addon?._id || addon?.id;
            const name = addon?.name;
            if (id && name) {
              map.set(id.toString(), name);
            }
          });
          setAddonMap(map);
        }
      } catch (error) {
        console.warn("Failed to load add-ons for invoice preview:", error);
      }
    };
    loadAddons();
    return () => {
      ignore = true;
    };
  }, []);

  const invoiceAddonNames = useMemo(() => {
    if (!invoiceData) return [];
    return (invoiceData.service?.addOns || []).map((addonValue) => {
      if (typeof addonValue === "string") {
        return addonMap.get(addonValue) || addonValue;
      }
      if (addonValue && typeof addonValue === "object") {
        const id = addonValue?._id || addonValue?.id;
        const name = addonValue?.name || addonValue?.label;
        if (name) return name;
        if (id) return addonMap.get(id.toString()) || id.toString();
      }
      return String(addonValue);
    });
  }, [addonMap, invoiceData]);

  const invoiceIssueDate = useMemo(() => {
    if (!invoiceData) return null;
    const date = new Date(invoiceData.issuedAt);
    return Number.isNaN(date.getTime()) ? null : date;
  }, [invoiceData]);

  const invoiceAppointmentDate = useMemo(() => {
    if (invoiceData?.appointmentDate) {
      const date = new Date(invoiceData.appointmentDate);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
    }
    if (editingAppointment?.appointmentDate) {
      const date = new Date(editingAppointment.appointmentDate);
      if (!Number.isNaN(date.getTime())) return date;
    }
    return null;
  }, [editingAppointment?.appointmentDate, invoiceData?.appointmentDate]);

  const invoiceClientName =
    invoiceData?.snapshot?.clientName ||
    editingAppointment?.clientName ||
    "Client";

  const invoiceClientEmail =
    invoiceData?.snapshot?.clientEmail || editingAppointment?.clientEmail || "";

  const invoiceClientPhone =
    invoiceData?.snapshot?.clientPhone || editingAppointment?.clientPhone || "";

  const invoiceClientDobFormatted = useMemo(() => {
    const dobSource =
      invoiceData?.snapshot?.clientDob || editingAppointment?.clientDob;
    if (!dobSource) return "N/A";
    const dob = new Date(dobSource);
    return Number.isNaN(dob.getTime())
      ? typeof dobSource === "string"
        ? dobSource
        : "N/A"
      : dob.toLocaleDateString();
  }, [invoiceData?.snapshot?.clientDob, editingAppointment?.clientDob]);

  const invoiceTherapistName =
    invoiceData?.snapshot?.therapistName ||
    editingAppointment?.therapist ||
    "N/A";

  const invoiceTherapistClinic =
    invoiceData?.snapshot?.therapistClinic ||
    editingAppointment?.clinic?.clinicName ||
    editingAppointment?.clinic?.name ||
    "N/A";

  const invoiceTherapistCrmta =
    invoiceData?.snapshot?.therapistCrmtaNumber || "N/A";

  const invoicePaymentMethod =
    invoiceData?.paymentMethod ||
    editingAppointment?.paymentMethod ||
    "Not recorded";

  const invoiceNumber = invoiceData?.invoiceNumber || "Invoice";

  const invoiceDurationMinutes =
    invoiceData?.service?.durationMinutes ?? editingAppointment?.duration ?? 60;

  const invoiceClinicEmail =
    invoiceData?.snapshot?.clinicEmail ||
    editingAppointment?.clinic?.email ||
    "";

  const invoiceClinicPhone =
    invoiceData?.snapshot?.clinicPhone ||
    editingAppointment?.clinic?.phone ||
    "";

  const invoiceServiceName =
    invoiceData?.service?.name ||
    editingAppointment?.service ||
    "Therapy session";

  const invoiceServiceAmount = roundCurrency(
    invoiceData?.totals?.serviceAmount ?? 0,
  );
  const invoiceAddonAmount = roundCurrency(
    invoiceData?.totals?.addonAmount ?? 0,
  );
  const invoiceSubtotal = roundCurrency(invoiceData?.totals?.subtotal ?? 0);
  const invoiceServiceTax = roundCurrency(invoiceData?.totals?.serviceTax ?? 0);
  const invoiceServiceFee = roundCurrency(invoiceData?.totals?.serviceFee ?? 0);
  const invoiceTipAmount = roundCurrency(invoiceData?.totals?.tipAmount ?? 0);
  const invoiceTotal = useMemo(
    () => roundCurrency(invoiceSubtotal + invoiceServiceTax + invoiceTipAmount),
    [invoiceServiceTax, invoiceSubtotal, invoiceTipAmount],
  );

  const invoiceNotes = invoiceData?.notes || editingAppointment?.notes || "";

  const invoiceIssuedAtDisplay = invoiceIssueDate
    ? invoiceIssueDate.toLocaleDateString()
    : "--";

  const invoiceAppointmentDateDisplay = invoiceAppointmentDate
    ? invoiceAppointmentDate.toLocaleDateString()
    : "--";

  const handleDownloadInvoice = useCallback(async () => {
    if (!invoiceRef.current || !invoiceData || invoiceLoading) {
      toast({
        title: "Invoice is not ready",
        description: "Please wait for the invoice to finish loading.",
      });
      return;
    }

    setIsGeneratingInvoice(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imageData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "pt",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgProps = pdf.getImageProperties(imageData);
      const ratio = Math.min(
        pageWidth / imgProps.width,
        pageHeight / imgProps.height,
      );
      const imgWidth = imgProps.width * ratio;
      const imgHeight = imgProps.height * ratio;
      const marginX = (pageWidth - imgWidth) / 2;
      const marginY = (pageHeight - imgHeight) / 2;

      pdf.addImage(imageData, "PNG", marginX, marginY, imgWidth, imgHeight);
      const safeClientName = sanitizeFilePart(invoiceClientName);
      pdf.save(`Invoice_${safeClientName}.pdf`);
      toast({
        title: "Invoice downloaded",
        description: "The invoice PDF has been saved to your device.",
      });
    } catch (error) {
      console.error("Failed to generate invoice PDF:", error);
      toast({
        title: "Download failed",
        description: "We couldn’t generate the invoice PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingInvoice(false);
    }
  }, [invoiceClientName, invoiceData, invoiceLoading, toast]);

  const stats = [
    {
      label: "Upcoming sessions",
      value: upcomingCount,
      description: "Within the next 30 days",
      icon: CalendarCheck,
    },
    {
      label: "Favourite therapists",
      value: favoritesCount,
      description: "Curated by your AI matches",
      icon: Heart,
    },
  ];

  return (
    <main className="relative min-h-screen bg-gradient-to-b from-palette-cloud via-white to-white">
      <NavbarDemo />

      {/* Decorative gradient background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-gradient-to-b from-palette-navy/5 via-transparent to-transparent"
      />

      {/* Main content */}
      <section className="relative z-10 mx-auto max-w-7xl px-3 pb-24 pt-10 sm:px-6 sm:pt-16 lg:px-8">
        {/* Header Section */}
        <header className="mb-8 sm:mb-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div className="space-y-3 sm:space-y-4">
              <Badge className="bg-palette-lime/15 text-palette-navy border-palette-lime/30 px-4 py-1.5 hover:bg-palette-lime/30 hover:text-palette-navy">
                <Sparkles className="h-3.5 w-3.5 mr-2" />
                Dashboard
              </Badge>
              <div className="space-y-2">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-palette-phantom">
                  Welcome back,{" "}
                  <span className="text-palette-navy">{accountHolderName}</span>
                </h1>
                {nextAppointment ? (
                  <p className="text-lg text-palette-space max-w-2xl">
                    Your next session with{" "}
                    <span className="font-semibold text-palette-phantom">
                      {nextAppointment.therapist}
                    </span>{" "}
                    is on {formatDateLabel(nextAppointment.startsAt)} at{" "}
                    {formatTimeLabel(nextAppointment.startsAt)}.
                  </p>
                ) : (
                  <p className="text-lg text-palette-space max-w-2xl">
                    Book a new session to keep your therapeutic momentum going.
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3 rounded-2xl bg-white/80 backdrop-blur-sm border border-palette-smoke/50 px-5 py-3 shadow-lg">
                <Avatar className="h-12 w-12 border-2 border-palette-lime/30">
                  <AvatarFallback className="bg-palette-navy font-semibold text-white text-base">
                    {getInitials(accountHolderName)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="text-sm font-semibold text-palette-phantom">
                    {accountHolderName}
                  </p>
                  <p className="text-xs text-palette-space">{accountEmail}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate("/book")}
                  className="bg-palette-navy hover:bg-palette-phantom text-white rounded-xl px-6 shadow-lg hover:shadow-xl transition-all">
                  Book a session
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-palette-phantom hover:bg-palette-cloud rounded-xl">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Pending Reassignment Alert Banner */}
        {pendingReassignments.length > 0 && (
          <Card className="mb-6 border-l-4 border-l-amber-500 bg-amber-50/80 backdrop-blur-sm shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-600" />
                    <h3 className="font-semibold text-amber-900">
                      Action Required: Appointment Reassignment
                    </h3>
                  </div>
                  <p className="text-amber-800 mb-3">
                    {pendingReassignments.length === 1
                      ? "Your appointment has been reassigned to a new therapist. Please review and choose to accept or request a refund."
                      : `${pendingReassignments.length} of your appointments have been reassigned. Please review each one.`}
                  </p>
                  {pendingReassignments[0]?.decisionDeadline && (
                    <p className="text-sm text-amber-700">
                      Please respond by{" "}
                      {new Date(
                        pendingReassignments[0].decisionDeadline,
                      ).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                      . If no action is taken, a full refund will be processed
                      automatically.
                    </p>
                  )}
                </div>
                <Button
                  onClick={() => {
                    setSelectedReassignment(pendingReassignments[0]);
                    setReassignmentModalOpen(true);
                  }}
                  className="bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap">
                  Review Reassignment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-3 mb-12">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.label}
                className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden group">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                  <CardTitle className="text-sm font-medium text-palette-space uppercase tracking-wide">
                    {stat.label}
                  </CardTitle>
                  <div className="rounded-xl bg-palette-lime/15 p-2.5 text-palette-navy group-hover:bg-palette-lime group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl sm:text-4xl font-bold text-palette-phantom mb-1">
                    {stat.value}
                  </div>
                  <p className="text-sm text-palette-space">
                    {stat.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Next Appointment Card */}
        {nextAppointment && (
          <Card className="mb-12 border-none bg-gradient-to-br from-palette-navy via-palette-phantom to-palette-navy text-white shadow-2xl rounded-3xl overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-br from-palette-lime/10 via-transparent to-transparent" />
            <div className="relative z-10">
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between pb-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-palette-lime text-palette-navy border-none font-semibold px-4 py-1.5">
                      {nextAppointment.status}
                    </Badge>
                    <Badge
                      variant="outline"
                      className="bg-white/10 text-white border-white/20">
                      Upcoming
                    </Badge>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                    Next session with {nextAppointment.therapist}
                  </CardTitle>
                  <CardDescription className="text-white/90 text-base">
                    {formatDateLabel(nextAppointment.startsAt)} •{" "}
                    {formatTimeLabel(nextAppointment.startsAt)} •{" "}
                    {nextAppointment.duration}
                  </CardDescription>
                </div>
                <div className="flex flex-col gap-3 text-sm text-white/90">
                  <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
                    <MapPin className="h-5 w-5 text-palette-lime" />{" "}
                    {nextAppointment.location}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between pb-6">
                <div className="flex flex-wrap gap-2">
                  {nextAppointment.focus.map((tag) => (
                    <Badge
                      key={tag}
                      className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex flex-col xs:flex-row sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full md:w-auto">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="lg"
                            variant="outline"
                            disabled={
                              !canModifyAppointment(nextAppointment.startsAt)
                            }
                            className="border-white/30 text-white hover:bg-white/20 hover:text-white rounded-xl px-6 disabled:opacity-60"
                            onClick={() => openRescheduleFor(nextAppointment)}>
                            Reschedule
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canModifyAppointment(nextAppointment.startsAt) && (
                        <TooltipContent>
                          Rescheduling or cancellation not allowed within 2
                          hours of appointment.
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            size="lg"
                            variant="outline"
                            disabled={
                              !canModifyAppointment(nextAppointment.startsAt)
                            }
                            className="border-white/30 text-white hover:bg-white/20 hover:text-white rounded-xl px-6 disabled:opacity-60"
                            onClick={() => openCancelFor(nextAppointment.id)}>
                            Cancel
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canModifyAppointment(nextAppointment.startsAt) && (
                        <TooltipContent>
                          Rescheduling or cancellation not allowed within 2
                          hours of appointment.
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </CardContent>
              {nextAppointment.notes && (
                <CardFooter className="pt-0 pb-6 text-base text-white/80 border-t border-white/10">
                  {nextAppointment.notes}
                </CardFooter>
              )}
            </div>
          </Card>
        )}

        {/* Appointments Section */}
        <section className="mb-16 space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-palette-phantom mb-2">
                Appointments
              </h2>
              <p className="text-base text-palette-space">
                Keep track of upcoming sessions and reflect on your progress.
              </p>
            </div>
            <Button
              onClick={() => navigate("/book")}
              className="bg-palette-navy hover:bg-palette-phantom text-white rounded-xl px-6 shadow-lg">
              Book another match
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          <Tabs defaultValue="upcoming" className="w-full">
            <TabsList className="bg-palette-cloud border border-palette-smoke/50 rounded-xl p-1.5 inline-flex">
              <TabsTrigger
                value="upcoming"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-palette-navy data-[state=active]:shadow-md">
                Upcoming
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-palette-navy data-[state=active]:shadow-md">
                History
              </TabsTrigger>
            </TabsList>
            <TabsContent value="upcoming" className="mt-6">
              {isLoadingAppointments ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex items-center justify-center">
                    <p className="text-palette-space">
                      Loading appointments...
                    </p>
                  </div>
                </Card>
              ) : appointmentsError ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <p className="text-palette-phantom font-semibold">
                      Failed to load appointments
                    </p>
                    <p className="text-sm text-palette-space">
                      {appointmentsError}
                    </p>
                  </div>
                </Card>
              ) : upcomingAppointments.length === 0 ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex flex-col items-center justify-center gap-6">
                    <div className="rounded-full bg-palette-lime/10 p-6">
                      <CalendarCheck className="h-12 w-12 text-palette-lime" />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-palette-phantom mb-2">
                        No upcoming appointments
                      </p>
                      <p className="text-palette-space">
                        Book a session to get started
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate("/book")}
                      className="bg-palette-navy hover:bg-palette-phantom text-white rounded-xl">
                      Book a session
                    </Button>
                  </div>
                </Card>
              ) : (
                <div className="grid gap-6 lg:grid-cols-2">
                  {upcomingAppointments.map((appointment) => (
                    <Card
                      key={appointment.id}
                      className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                      <CardHeader className="space-y-3 pb-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <CardTitle className="text-xl font-bold text-palette-phantom mb-1">
                              {appointment.therapist}
                            </CardTitle>
                            <CardDescription className="text-palette-space">
                              {appointment.therapistTagline}
                            </CardDescription>
                          </div>
                          <Badge className="bg-palette-lime/15 text-palette-navy border-palette-lime/30 whitespace-nowrap">
                            {appointment.status}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-palette-space pt-2 border-t border-palette-smoke/30">
                          <span className="inline-flex items-center gap-2">
                            <CalendarCheck className="h-4 w-4 text-palette-navy" />
                            {formatDateLabel(appointment.startsAt)}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            <Clock className="h-4 w-4 text-palette-navy" />
                            {formatTimeLabel(appointment.startsAt)} ·{" "}
                            {appointment.duration}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                          {appointment.focus.map((tag) => (
                            <Badge
                              key={tag}
                              className="bg-palette-cloud text-palette-phantom border-palette-smoke/50">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <div className="grid gap-2 text-sm text-palette-space pt-2 border-t border-palette-smoke/30">
                          <span className="inline-flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-palette-lime" />
                            {appointment.location}
                          </span>
                        </div>
                      </CardContent>
                      <CardFooter className="flex items-center justify-between pt-4 border-t border-palette-smoke/30 bg-palette-cloud/30">
                        {appointment.notes && (
                          <p className="text-sm text-palette-space max-w-sm line-clamp-1">
                            {appointment.notes}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-palette-smoke text-palette-phantom hover:bg-palette-navy hover:text-white rounded-lg"
                            onClick={async () => {
                              try {
                                await api.appointments.sendAppointmentEmail(
                                  appointment.id,
                                );
                                toast({
                                  title: "Email sent",
                                  description:
                                    "Appointment details have been sent to your inbox.",
                                });
                              } catch (error) {
                                toast({
                                  title: "Error",
                                  description:
                                    "Failed to send appointment email. Please try again.",
                                  variant: "destructive",
                                });
                              }
                            }}>
                            Send appointment email
                          </Button>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={
                                      !canModifyAppointment(
                                        appointment.startsAt,
                                      )
                                    }
                                    className="border-palette-smoke text-palette-phantom hover:bg-palette-navy hover:text-white rounded-lg disabled:opacity-60"
                                    onClick={() =>
                                      openRescheduleFor(appointment)
                                    }>
                                    Reschedule
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!canModifyAppointment(appointment.startsAt) && (
                                <TooltipContent>
                                  Rescheduling or cancellation not allowed
                                  within 2 hours of appointment.
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={
                                      !canModifyAppointment(
                                        appointment.startsAt,
                                      )
                                    }
                                    className="border-palette-smoke text-palette-phantom hover:bg-palette-navy hover:text-white rounded-lg disabled:opacity-60"
                                    onClick={() =>
                                      openCancelFor(appointment.id)
                                    }>
                                    Cancel
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {!canModifyAppointment(appointment.startsAt) && (
                                <TooltipContent>
                                  Rescheduling or cancellation not allowed
                                  within 2 hours of appointment.
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="history" className="mt-6">
              {isLoadingAppointments ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex items-center justify-center">
                    <p className="text-palette-space">Loading history...</p>
                  </div>
                </Card>
              ) : appointmentsError ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <p className="text-palette-phantom font-semibold">
                      Failed to load appointment history
                    </p>
                    <p className="text-sm text-palette-space">
                      {appointmentsError}
                    </p>
                  </div>
                </Card>
              ) : historyAppointments.length === 0 ? (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
                  <div className="flex flex-col items-center justify-center gap-6">
                    <div className="rounded-full bg-palette-navy/10 p-6">
                      <Clock className="h-12 w-12 text-palette-navy" />
                    </div>
                    <div className="text-center">
                      <p className="text-xl font-semibold text-palette-phantom mb-2">
                        No appointment history
                      </p>
                      <p className="text-palette-space">
                        Your completed sessions will appear here
                      </p>
                    </div>
                  </div>
                </Card>
              ) : (
                <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-palette-smoke/50 hover:bg-palette-cloud/30">
                          <TableHead className="pl-6 text-palette-phantom font-semibold">
                            Session
                          </TableHead>
                          <TableHead className="text-palette-phantom font-semibold">
                            Date
                          </TableHead>
                          <TableHead className="text-palette-phantom font-semibold">
                            Status
                          </TableHead>
                          <TableHead className="text-palette-phantom font-semibold">
                            Focus
                          </TableHead>
                          <TableHead className="text-palette-phantom font-semibold">
                            Shift
                          </TableHead>
                          <TableHead className="text-right text-palette-phantom font-semibold">
                            Invoice
                          </TableHead>
                          <TableHead className="pr-6 text-right text-palette-phantom font-semibold">
                            Rating
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {historyAppointments.map((history) => (
                          <TableRow
                            key={history.id}
                            className="border-palette-smoke/30 hover:bg-palette-cloud/50 transition-colors">
                            <TableCell className="pl-6">
                              <div className="font-semibold text-palette-phantom">
                                {history.therapist}
                              </div>
                              <div className="text-xs text-palette-space">
                                {history.id}
                              </div>
                            </TableCell>
                            <TableCell className="text-palette-space">
                              {formatDateLabel(history.completedAt)}
                            </TableCell>
                            <TableCell>
                              {history.status === "Cancelled" ? (
                                <Badge className="bg-palette-navy/10 text-palette-navy border-palette-navy/20">
                                  Cancelled
                                </Badge>
                              ) : (
                                <Badge className="bg-palette-lime/15 text-palette-navy border-palette-lime/30">
                                  Completed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-palette-space">
                              {history.focus}
                            </TableCell>
                            <TableCell>
                              {history.moodShift ? (
                                <Badge className="bg-palette-lime/15 text-palette-navy border-palette-lime/30">
                                  {history.moodShift}
                                </Badge>
                              ) : (
                                <span className="text-palette-space text-sm">
                                  -
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {history.status === "Completed" ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-palette-smoke text-palette-phantom hover:bg-palette-navy hover:text-white rounded-lg"
                                  onClick={() =>
                                    openInvoiceForBooking(history.id)
                                  }
                                  disabled={invoiceLoading}>
                                  Invoice
                                </Button>
                              ) : (
                                <span className="text-sm text-palette-space">
                                  -
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="pr-6 text-right">
                              <span className="inline-flex items-center gap-1.5 font-semibold text-palette-phantom">
                                <Heart className="h-4 w-4 text-palette-lime fill-palette-lime" />
                                {history.rating.toFixed(1)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </section>

        {/* Favourite Therapists Section */}
        <section className="mb-16 space-y-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-palette-phantom mb-2">
              Favourite therapists
            </h2>
            <p className="text-base text-palette-space">
              Curated by your AI matching rituals for continuity and chemistry.
            </p>
          </div>
          {isLoadingFavorites ? (
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
              <div className="flex items-center justify-center">
                <p className="text-palette-space">
                  Loading favorite therapists...
                </p>
              </div>
            </Card>
          ) : favoritesError ? (
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
              <div className="flex flex-col items-center justify-center gap-3">
                <p className="text-palette-phantom font-semibold">
                  Failed to load favorites
                </p>
                <p className="text-sm text-palette-space">{favoritesError}</p>
              </div>
            </Card>
          ) : favoriteTherapists.length === 0 ? (
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
              <div className="flex flex-col items-center justify-center gap-6">
                <div className="rounded-full bg-palette-lime/10 p-6">
                  <Heart className="h-12 w-12 text-palette-lime" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-palette-phantom mb-2">
                    No favorite therapists yet
                  </p>
                  <p className="text-palette-space">
                    Your favorited therapists will appear here
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {favoriteTherapists.map((therapist) => {
                const first = therapist.name.split(" ")[0];
                return (
                  <Card
                    key={therapist.id}
                    className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                    <CardHeader className="flex flex-col gap-4 pb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          <Avatar className="h-14 w-14 border-2 border-palette-lime/30">
                            {therapist.profilePhotoUrl ? (
                              <img
                                src={therapist.profilePhotoUrl}
                                alt={therapist.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <AvatarFallback
                                className={cn(
                                  "font-semibold text-base",
                                  therapist.avatarClass,
                                )}>
                                {getInitials(therapist.name)}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-lg font-bold text-palette-phantom truncate">
                              {therapist.name}
                            </CardTitle>
                            <CardDescription className="text-palette-space truncate">
                              {therapist.title}
                            </CardDescription>
                          </div>
                        </div>
                        <Badge className="bg-palette-lime text-palette-navy border-none font-semibold whitespace-nowrap">
                          {therapist.matchScore}%
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                      <div className="flex items-center justify-between py-2 border-t border-palette-smoke/30">
                        <span className="text-palette-space">Last session</span>
                        <span
                          className={cn(
                            "font-semibold",
                            therapist.lastSession
                              ? "text-palette-phantom"
                              : "text-palette-space italic",
                          )}>
                          {therapist.lastSession || "No previous sessions"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between py-2 border-t border-palette-smoke/30">
                        <span className="text-palette-space">
                          Client rating
                        </span>
                        {therapist.reviewCount > 0 ? (
                          <span className="inline-flex items-center gap-1.5 font-semibold text-palette-phantom">
                            <Heart className="h-4 w-4 text-palette-lime fill-palette-lime" />
                            {therapist.rating.toFixed(1)} (
                            {therapist.reviewCount}{" "}
                            {therapist.reviewCount === 1 ? "review" : "reviews"}
                            )
                          </span>
                        ) : (
                          <span className="text-sm text-palette-space italic">
                            No reviews yet
                          </span>
                        )}
                      </div>
                    </CardContent>
                    <CardFooter className="flex items-center justify-between pt-4 border-t border-palette-smoke/30 bg-palette-cloud/30">
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-palette-space mb-1">
                          Next availability
                        </p>
                        <p
                          className={cn(
                            "text-sm font-semibold",
                            therapist.nextAvailable
                              ? "text-palette-phantom"
                              : "text-palette-space italic",
                          )}>
                          {therapist.nextAvailable || "Check availability"}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-palette-navy hover:bg-palette-phantom text-white rounded-lg px-4"
                        onClick={() => {
                          setSelectedTherapistForBooking(therapist);
                          setBookingModalOpen(true);
                        }}>
                        Book {first}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        {/* Saved Card Section */}
        <section id="payment-section" className="mb-16 space-y-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-palette-phantom mb-2">
              Saved payment method
            </h2>
            <p className="text-base text-palette-space">
              Manage your saved payment method for easy booking.
            </p>
          </div>

          {isLoadingCard ? (
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
              <div className="flex items-center justify-center">
                <p className="text-palette-space">Loading payment method...</p>
              </div>
            </Card>
          ) : savedCard ? (
            <Card className="border-none bg-gradient-to-br from-palette-navy via-palette-phantom to-palette-navy text-white shadow-2xl rounded-2xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-palette-lime/10 via-transparent to-transparent" />
              <div className="relative z-10">
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className="w-fit bg-palette-lime text-palette-navy border-none font-semibold capitalize">
                      {savedCard.brand?.toUpperCase() || "Card"}
                    </Badge>
                    {savedCard.isDefault && (
                      <Badge className="bg-white/20 text-white border-white/30">
                        Default
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-2xl font-bold text-white">
                    •••• •••• •••• {savedCard.last4}
                  </CardTitle>
                  <CardDescription className="text-white/90 text-base">
                    Expires{" "}
                    {savedCard.expMonth && savedCard.expYear
                      ? `${String(savedCard.expMonth).padStart(
                          2,
                          "0",
                        )}/${String(savedCard.expYear).slice(-2)}`
                      : "N/A"}
                  </CardDescription>
                </CardHeader>
                <CardFooter className="pt-6">
                  <Button
                    size="lg"
                    className="bg-palette-lime hover:bg-palette-lime/90 text-palette-navy font-semibold rounded-xl w-full shadow-lg"
                    onClick={() => setUpdateCardDialog({ open: true })}>
                    Update card
                  </Button>
                </CardFooter>
              </div>
            </Card>
          ) : (
            <Card className="border border-palette-smoke/50 bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg">
              <div className="flex flex-col items-center justify-center gap-6">
                <div className="rounded-full bg-palette-navy/10 p-6">
                  <CreditCard className="h-12 w-12 text-palette-navy" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-palette-phantom mb-2">
                    No saved payment method
                  </p>
                  <p className="text-palette-space">
                    Add a payment method when booking your next appointment
                  </p>
                </div>
              </div>
            </Card>
          )}
        </section>
      </section>

      {/* Invoice dialog */}
      <Dialog
        open={isInvoiceOpen}
        onOpenChange={(open) => {
          setIsInvoiceOpen(open);
          if (!open) {
            setInvoiceError(null);
            setIsGeneratingInvoice(false);
            setInvoiceLoading(false);
          }
        }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Invoice</DialogTitle>
            <DialogDescription>
              Review your session invoice and download a PDF copy.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {invoiceError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {invoiceError}
              </div>
            )}
            <div className="rounded-2xl border border-palette-smoke/50 bg-white shadow-sm">
              {invoiceLoading ? (
                <div className="flex items-center justify-center py-12">
                  <p className="text-palette-space">Loading invoice...</p>
                </div>
              ) : invoiceData ? (
                <div
                  ref={invoiceRef}
                  className="rounded-2xl bg-white p-8 text-slate-900">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div className="space-y-2">
                      <img
                        src="/logo-black.png"
                        alt="Book a Therapy"
                        width={160}
                        height={48}
                        className="h-12 w-auto object-contain"
                        crossOrigin="anonymous"
                      />
                      <p className="text-sm text-slate-600">
                        {invoiceTherapistClinic}
                      </p>
                      {invoiceClinicEmail && (
                        <p className="text-sm text-slate-600">
                          {invoiceClinicEmail}
                        </p>
                      )}
                      {invoiceClinicPhone && (
                        <p className="text-sm text-slate-600">
                          {invoiceClinicPhone}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2 text-right">
                      <h2 className="text-2xl font-semibold text-slate-900">
                        Invoice
                      </h2>
                      <p className="text-sm text-slate-600">
                        Invoice #: {invoiceNumber}
                      </p>
                      <p className="text-sm text-slate-600">
                        Issued: {invoiceIssuedAtDisplay}
                      </p>
                      <p className="text-sm text-slate-600">
                        Appointment: {invoiceAppointmentDateDisplay}
                        {editingAppointment?.startTime &&
                          editingAppointment?.endTime && (
                            <>
                              {" "}
                              • {editingAppointment.startTime} –{" "}
                              {editingAppointment.endTime}
                            </>
                          )}
                      </p>
                      <p className="text-sm text-slate-600">
                        Payment method: {invoicePaymentMethod}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8 grid gap-6 rounded-lg border border-slate-200 p-6 md:grid-cols-2">
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Client
                      </h3>
                      <dl className="mt-3 space-y-2 text-sm text-slate-700">
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Name</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceClientName}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Email</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceClientEmail || "N/A"}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Phone</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceClientPhone || "N/A"}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">DOB</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceClientDobFormatted}
                          </dd>
                        </div>
                      </dl>
                    </div>
                    <div>
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Therapist &amp; Clinic
                      </h3>
                      <dl className="mt-3 space-y-2 text-sm text-slate-700">
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Therapist</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceTherapistName}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">Clinic</dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceTherapistClinic}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-4">
                          <dt className="text-slate-600">
                            CRMTA / Registration
                          </dt>
                          <dd className="font-medium text-slate-900">
                            {invoiceTherapistCrmta || "N/A"}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  <div className="mt-6 overflow-hidden rounded-lg border border-slate-200">
                    <div className="bg-slate-50 px-6 py-3 text-sm font-semibold text-slate-700">
                      Appointment details
                    </div>
                    <div className="space-y-4 px-6 py-5 text-sm text-slate-700">
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-600">Service</span>
                        <span className="font-medium text-slate-900">
                          {invoiceServiceName}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-slate-600">Duration</span>
                        <span className="font-medium text-slate-900">
                          {invoiceDurationMinutes
                            ? `${invoiceDurationMinutes} min`
                            : "N/A"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-600">Add-ons</span>
                        <p className="mt-1 font-medium text-slate-900">
                          {invoiceAddonNames.length
                            ? invoiceAddonNames.join(", ")
                            : "None"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-6 rounded-lg border border-slate-200 p-6 md:grid-cols-2">
                    <div className="space-y-2 text-sm text-slate-700">
                      <div className="flex justify-between gap-4">
                        <span>Service amount</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(invoiceServiceAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Add-on amount</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(invoiceAddonAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Subtotal</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(invoiceSubtotal)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Service tax (5%)</span>
                        <span className="font-medium text-slate-900">
                          {formatCurrency(invoiceServiceTax)}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Service fee</span>
                        <span
                          className="font-medium text-slate-400"
                          style={{
                            textDecorationLine: "line-through",
                            textDecorationColor: "currentColor",
                          }}>
                          {formatCurrency(invoiceServiceFee)}
                        </span>
                      </div>
                      {invoiceTipAmount > 0 && (
                        <div className="flex justify-between gap-4">
                          <span>Tip</span>
                          <span className="font-medium text-slate-900">
                            {formatCurrency(invoiceTipAmount)}
                          </span>
                        </div>
                      )}
                      <div className="mt-3 flex justify-between gap-4 border-t border-slate-200 pt-3 text-base font-semibold text-slate-900">
                        <span>
                          Total (subtotal + tax
                          {invoiceTipAmount > 0 ? " + tip" : ""})
                        </span>
                        <span>{formatCurrency(invoiceTotal)}</span>
                      </div>
                    </div>
                    <div className="space-y-4 text-sm text-slate-700">
                      <div>
                        <p className="font-semibold text-slate-900">
                          Payment Method
                        </p>
                        <p>{invoicePaymentMethod}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          Service Tax
                        </p>
                        <p>Applied at 5% of the service subtotal.</p>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">
                          Service Fee
                        </p>
                        <p
                          className="text-slate-500"
                          style={{
                            textDecorationLine: "line-through",
                            textDecorationColor: "currentColor",
                          }}>
                          A flat {formatCurrency(invoiceServiceFee)} platform
                          fee (currently waived).
                        </p>
                      </div>
                      <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                        Service tax is included in the total above. Service fee
                        is displayed for reference only and is not collected at
                        this time. Thank you for choosing Book a Therapy for
                        your wellness care.
                      </div>
                    </div>
                  </div>

                  {invoiceNotes && (
                    <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
                      {invoiceNotes}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <p className="text-palette-space">
                    Select a completed appointment to view its invoice.
                  </p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsInvoiceOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleDownloadInvoice}
              disabled={isGeneratingInvoice || invoiceLoading || !invoiceData}>
              {isGeneratingInvoice ? "Generating..." : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Card Modal */}
      <Dialog
        open={updateCardDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setUpdateCardDialog({ open: false });
          }
        }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Update payment method</DialogTitle>
            <DialogDescription>
              Update your saved payment method for future bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 overflow-y-auto">
            <PaymentMethodCollection
              clientEmail={accountEmail}
              clientName={accountHolderName}
              onSuccess={async (paymentInfo) => {
                toast({
                  title: "Payment method updated",
                  description:
                    "Your payment method has been successfully updated.",
                });
                setUpdateCardDialog({ open: false });
                // Refresh saved card
                try {
                  const data = await api.payment.getSavedPaymentMethod(
                    accountEmail,
                  );
                  if (data?.paymentMethod) {
                    const pm = data.paymentMethod;
                    setSavedCard({
                      brand: pm.card?.brand || "Card",
                      last4: pm.card?.last4 || "",
                      expMonth: pm.card?.exp_month || null,
                      expYear: pm.card?.exp_year || null,
                      isDefault: data.isDefault || false,
                    });
                  } else {
                    setSavedCard(null);
                  }
                } catch (error) {
                  console.error("Failed to refresh saved card:", error);
                }
              }}
              onError={(errorMessage) => {
                toast({
                  title: "Error",
                  description:
                    errorMessage || "Failed to update payment method",
                  variant: "destructive",
                });
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <AlertDialog
        open={cancelDialog.open}
        onOpenChange={(open) => {
          if (!open) closeCancelDialog();
        }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel appointment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this appointment? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={closeCancelDialog}>
              Keep appointment
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCancel}
              disabled={isCancelling}
              className="bg-red-600 hover:bg-red-700 text-white">
              {isCancelling ? "Cancelling..." : "Cancel appointment"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule modal */}
      <Dialog
        open={rescheduleDialog.open}
        onOpenChange={(open) => (open ? null : closeRescheduleDialog())}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Reschedule appointment
            </DialogTitle>
            <DialogDescription className="font-medium">
              Drag your appointment block to a new date and time. Your session
              with {rescheduleDialog.appointment?.therapist}.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-hidden p-4">
            {rescheduleDialog.appointment && (
              <ScheduleView
                startDate={scheduleStartDate}
                therapistName={rescheduleDialog.appointment.therapist}
                currentAppointment={{
                  startsAt: rescheduleDialog.appointment.startsAt,
                  durationMins: rescheduleDialog.appointment.duration
                    ? parseInt(
                        rescheduleDialog.appointment.duration.replace(
                          /\D/g,
                          "",
                        ),
                      ) || 60
                    : 60,
                  therapist: rescheduleDialog.appointment.therapist,
                  status: rescheduleDialog.appointment.status,
                }}
                onAppointmentMove={handleAppointmentMove}
                scheduleData={scheduleData}
                isLoading={isLoadingSlots}
                operatingHours={operatingHours}
                therapistAvailability={therapistAvailability}
                onWeekChange={(newDate) => {
                  setScheduleStartDate(newDate);
                  if (rescheduleDialog.appointment?.therapistId) {
                    fetchTherapistScheduleData(rescheduleDialog.appointment);
                  }
                }}
              />
            )}
          </div>

          <DialogFooter className="flex items-center justify-between border-t pt-4">
            <Button variant="ghost" onClick={closeRescheduleDialog}>
              Cancel
            </Button>
            <p className="text-sm text-palette-space">
              {isRescheduling && "Rescheduling..."}
            </p>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Booking Modal */}
      <EnhancedBookingModal
        open={bookingModalOpen}
        onOpenChange={setBookingModalOpen}
        therapist={selectedTherapistForBooking}
        // Services
        availableServices={availableServices}
        selectedService={selectedService}
        onServiceSelect={(service) => {
          setSelectedService(service);
          setSelectedDuration(null); // Reset duration when service changes
        }}
        isLoadingServices={isLoadingBookingData}
        // Duration
        selectedDuration={selectedDuration}
        onDurationSelect={setSelectedDuration}
        // Payment
        paymentInfo={paymentInfo}
        onPaymentSuccess={handlePaymentSuccess}
        bookingError={bookingError}
        onErrorChange={setBookingError}
        clientEmail={accountEmail}
        clientName={user?.fullName || accountEmail}
        // Settings
        selectedSetting={selectedSetting}
        onSettingSelect={setSelectedSetting}
        // Date & Time
        bookingDate={bookingDate}
        onDateSelect={setBookingDate}
        bookingTime={bookingTime}
        onTimeSelect={setBookingTime}
        availableSlots={bookingSlots}
        isLoadingSlots={isLoadingBookingData}
        // Payment Options
        paymentOption={paymentOption}
        onPaymentOptionChange={setPaymentOption}
        savedCard={savedCard}
        // Free Massage
        freeMassagesAvailable={freeMassagesAvailable}
        useFreeMassage={useFreeMassage}
        onUseFreeMassageChange={setUseFreeMassage}
        loadingRewards={loadingRewards}
        // Gift Card
        giftCardCode={giftCardCode}
        onGiftCardCodeChange={setGiftCardCode}
        onValidateGiftCard={handleValidateGiftCard}
        giftCardValidating={giftCardValidating}
        giftCardValid={giftCardValid}
        giftCardError={giftCardError}
        giftCardAmount={giftCardAmount}
        giftCardApplied={giftCardApplied}
        // Confirmation
        onConfirm={handleBookingConfirm}
        isSubmitting={isLoadingBookingData}
      />

      {/* Reassignment Approval Modal */}
      <ReassignmentApprovalModal
        open={reassignmentModalOpen}
        onOpenChange={setReassignmentModalOpen}
        reassignment={selectedReassignment}
        onAccept={handleAcceptReassignment}
        onReject={handleRejectReassignment}
      />
    </main>
  );
}
